/**
 * Tenant Overview (TMMPDM-0007/0017/0020): flat TenantView fields and period KPIs.
 * Edit… uses PATCH. Email-from fields stay read-only.
 * TMMPDM-0025: not `sellable` → an info Alert with the hint and its one action.
 * TMMPDM-0030 (tmm-api 0095): the alert renders `sellableReason` — its label, body and actions.
 */
import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, FormControl, InputLabel, MenuItem, Select, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { KpiCard } from '@/components/ui';
import { adminApi } from '@/api/tmm/admin';
import type { FleetDashboard, TenantDashboard, TenantView } from '@/api/tmm/types';
import { formatMoney, formatNumber } from '@/format';
import { periodFor, type PeriodKey } from '../dashboard/period';
import { EditTenantDialog } from './EditTenantDialog';
import { NotSellableAlert } from './Sellable';
import { PartnerDetailsSection } from '../partners/PartnerDetailsSection';

const PERIOD_KEYS: PeriodKey[] = ['THIS_MONTH', 'LAST_30_DAYS', 'LAST_MONTH'];

interface TenantOverviewPanelProps {
  tenant: TenantView;
  /** A reseller change succeeded — the page re-reads the tenant from tmm-api. */
  onResellerChanged: () => void;
  onUpdated: (tenant: TenantView) => void;
  tenantDashboard?: (slug: string, query: { from: string; to: string }) => Promise<FleetDashboard>;
  /** Partner Overview draws Details once, above this panel. */
  showDetails?: boolean;
  /** TMMPDM-0088: Partner Overview shows Can't sell in Needs you, so this panel can skip it. */
  showSellable?: boolean;
  editOpen?: boolean;
  onEditOpenChange?: (open: boolean) => void;
}

export function TenantOverviewPanel({
  tenant,
  onResellerChanged,
  onUpdated,
  tenantDashboard = adminApi.tenantDashboard,
  showDetails = true,
  showSellable = true,
  editOpen: editOpenProp,
  onEditOpenChange,
}: TenantOverviewPanelProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [periodKey, setPeriodKey] = useState<PeriodKey>('THIS_MONTH');
  const period = useMemo(() => periodFor(periodKey), [periodKey]);
  const [editOpenInternal, setEditOpenInternal] = useState(false);
  const editControlled = onEditOpenChange !== undefined;
  const editOpen = editControlled ? (editOpenProp ?? false) : editOpenInternal;
  const setEditOpen = (open: boolean) => {
    if (onEditOpenChange) onEditOpenChange(open);
    else setEditOpenInternal(open);
  };
  const [outcomeUnknown, setOutcomeUnknown] = useState(false);
  const requestKey = `${tenant.slug}:${period.from}:${period.to}`;
  const [dash, setDash] = useState<{ key: string; row: TenantDashboard | null }>({
    key: '',
    row: null,
  });

  useEffect(() => {
    let cancelled = false;
    tenantDashboard(tenant.slug, { from: period.from, to: period.to })
      .then((fleet) => {
        if (!cancelled) setDash({ key: requestKey, row: fleet.tenants[0] ?? null });
      })
      .catch(() => {
        if (!cancelled) setDash({ key: requestKey, row: null });
      });
    return () => {
      cancelled = true;
    };
  }, [tenant.slug, period.from, period.to, requestKey, tenantDashboard]);

  const dashCurrent = dash.key === requestKey;
  const figures = dashCurrent ? dash.row : null;
  const currency = tenant.currency;
  const dashValue = (value: string | number | undefined) =>
    figures == null ? t('tenantDetail.overview.kpi.noData', '—') : (value ?? '—');
  return (
    <Box>
      {showDetails ? (
        <PartnerDetailsSection tenant={tenant} brand="—" onEdit={() => setEditOpen(true)} />
      ) : null}
      {showSellable ? (
        <NotSellableAlert
          tenant={tenant}
          resellerName={
            tenant.reseller?.name ?? (tenant.reseller === null ? '' : `#${tenant.reseller.id}`)
          }
          onEditTenant={() => setEditOpen(true)}
        />
      ) : null}
      {outcomeUnknown ? (
        <Alert severity="warning" sx={{ mt: 1 }} onClose={() => setOutcomeUnknown(false)}>
          {t(
            'common.outcomeUnknown',
            "We couldn't confirm the change. The screen has been reloaded — check before trying again.",
          )}
        </Alert>
      ) : null}
      <EditTenantDialog
        open={editOpen}
        tenant={tenant}
        onClose={() => setEditOpen(false)}
        onUpdated={(updated) => {
          setEditOpen(false);
          setOutcomeUnknown(false);
          onUpdated(updated);
        }}
        onOutcomeUnknown={() => {
          setEditOpen(false);
          setOutcomeUnknown(true);
          onResellerChanged();
        }}
      />
      <Box sx={{ mt: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200, mb: 2 }}>
          <InputLabel id="tenant-overview-period">
            {t('tenantDetail.overview.period', 'Period')}
          </InputLabel>
          <Select
            labelId="tenant-overview-period"
            value={periodKey}
            label={t('tenantDetail.overview.period', 'Period')}
            onChange={(event) => setPeriodKey(event.target.value as PeriodKey)}
          >
            {PERIOD_KEYS.map((key) => (
              <MenuItem key={key} value={key}>
                {t(`dashboard.period.${key}`, key)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 160px' }}>
            <KpiCard
              label={t('tenantDetail.overview.kpi.activeSubscribers', 'Active subscribers')}
              value={dashValue(
                figures ? formatNumber(figures.subscribers.active, locale) : undefined,
              )}
              {...(figures
                ? {
                    hint: `${formatNumber(figures.subscribers.newInPeriod, locale)} / ${formatNumber(figures.subscribers.churnedInPeriod, locale)}`,
                  }
                : {})}
            />
          </Box>
          <Box sx={{ flex: '1 1 160px' }}>
            <KpiCard
              label={t('tenantDetail.overview.kpi.trialing', 'Trialing')}
              value={dashValue(
                figures ? formatNumber(figures.subscribers.trialing, locale) : undefined,
              )}
            />
          </Box>
          <Box sx={{ flex: '1 1 160px' }}>
            <KpiCard
              label={t('tenantDetail.overview.kpi.comped', 'Comped')}
              value={dashValue(
                figures ? formatNumber(figures.subscribers.comped, locale) : undefined,
              )}
            />
          </Box>
          <Box sx={{ flex: '1 1 160px' }}>
            <KpiCard
              label={t('tenantDetail.overview.kpi.guestSeats', 'Guest seats')}
              value={dashValue(
                figures ? formatNumber(figures.guests.activeSeats, locale) : undefined,
              )}
              {...(figures
                ? {
                    // TMMPDM-0044: `count` picks the plural form; `shown` is the formatted number.
                    hint: t('tenantDetail.overview.kpi.chargedSeats', '{{shown}} charged', {
                      count: figures.guests.chargedSeats,
                      shown: formatNumber(figures.guests.chargedSeats, locale),
                    }),
                  }
                : {})}
            />
          </Box>
          <Box sx={{ flex: '1 1 160px' }}>
            <KpiCard
              label={t('tenantDetail.overview.kpi.connectedAccounts', 'Connected accounts')}
              value={dashValue(
                figures ? formatNumber(figures.connectedAccounts, locale) : undefined,
              )}
            />
          </Box>
          <Box sx={{ flex: '1 1 160px' }}>
            <KpiCard
              label={t('tenantDetail.overview.kpi.retail', 'Retail')}
              value={dashValue(
                figures ? formatMoney(figures.revenue.retailMinor, currency, locale) : undefined,
              )}
            />
          </Box>
          <Box sx={{ flex: '1 1 160px' }}>
            <KpiCard
              label={t('tenantDetail.overview.kpi.tenantShare', 'Tenant share')}
              value={dashValue(
                figures
                  ? formatMoney(figures.revenue.tenantShareMinor, currency, locale)
                  : undefined,
              )}
            />
          </Box>
          <Box sx={{ flex: '1 1 160px' }}>
            <KpiCard
              label={t('tenantDetail.overview.kpi.platformShare', 'Platform share')}
              value={dashValue(
                figures
                  ? formatMoney(figures.revenue.platformShareMinor, currency, locale)
                  : undefined,
              )}
            />
          </Box>
          <Box sx={{ flex: '1 1 160px' }}>
            <KpiCard
              label={t('tenantDetail.overview.kpi.pendingPayout', 'Pending payout')}
              value={dashValue(
                figures ? formatMoney(figures.payouts.pendingMinor, currency, locale) : undefined,
              )}
              {...(figures
                ? {
                    hint: figures.payouts.nextCloseOn
                      ? t('tenantDetail.overview.kpi.nextClose', 'next close {{date}}', {
                          date: figures.payouts.nextCloseOn,
                        })
                      : '—',
                  }
                : {})}
            />
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
